import type { RootState } from '@/store';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';

import { selectBridgeRatesByTerm } from '@/store/slices/hybridInterestRateSlice';

interface BridgeTermSelectorProps {
  selectedTerm: number;
  onTermChange: (term: number) => void;
}

export function BridgeTermSelector({
  selectedTerm,
  onTermChange,
}: BridgeTermSelectorProps) {
  const bridgeRatesByTerm = useSelector((state: RootState) =>
    selectBridgeRatesByTerm(state)
  );

  const terms = [
    { value: 6, label: '6 Months' },
    { value: 12, label: '12 Months' },
    { value: 18, label: '18 Months' },
    { value: 24, label: '24 Months' },
  ];

  const getRateForTerm = (term: number): number | null => {
    if (!bridgeRatesByTerm) return null;

    switch (term) {
      case 6:
        return bridgeRatesByTerm.sixMonth;
      case 12:
        return bridgeRatesByTerm.twelveMonth;
      case 18:
        return bridgeRatesByTerm.eighteenMonth;
      case 24:
        return bridgeRatesByTerm.twentyFourMonth;
      default:
        return bridgeRatesByTerm.twelveMonth;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
        Bridge Loan Term
      </Typography>
      <RadioGroup
        value={selectedTerm.toString()}
        onChange={(e) => onTermChange(parseInt(e.target.value))}
        sx={{ color: 'white' }}
      >
        {terms.map((term) => {
          const rate = getRateForTerm(term.value);
          const rateLabel =
            rate != null && Number.isFinite(rate) ? `${rate.toFixed(3)}%` : '—';
          return (
            <FormControlLabel
              key={term.value}
              value={term.value.toString()}
              control={
                <Radio
                  sx={{
                    color: 'white',
                    '&.Mui-checked': { color: 'white' },
                  }}
                />
              }
              label={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    pr: 2,
                  }}
                >
                  <Typography sx={{ color: 'white' }}>{term.label}</Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {rateLabel}
                  </Typography>
                </Box>
              }
              sx={{ width: '100%', color: 'white' }}
            />
          );
        })}
      </RadioGroup>
      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
        Select the loan term. Bridge loans are interest-only with a balloon
        payment at the end.
      </Typography>
    </Box>
  );
}
